import { Dialog, IconButton, makeStyles, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import React from 'react';
import { useSelector } from 'react-redux';

import { getCurrentUser } from 'src/selectors/userSelectors';

import { colors } from '../../themes/colors';
import MHButton from '../MHButton';

const useStyles = makeStyles({
  title: {
    fontFamily: 'Avenir-Black',
    fontSize: 30,
    color: colors.black4,
    marginBottom: 10,
  },
  or: {
    fontFamily: 'Avenir-Black',
    fontSize: 20,
    color: colors.black4,
    marginBottom: 10,
    marginTop: 10,
    textAlign: 'center',
  },
  subTitle: {
    fontSize: 16,
    color: colors.gray5,
    marginBottom: 15,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: 40,
  },
  closeButton: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
  cardTitle: {
    fontFamily: 'Avenir-Black',
    fontSize: 18,
    color: colors.black3,
    marginBottom: 15,
  },
  cardTitleHighlighted: {
    color: '#009BAC',
  },
  cardText: {
    fontSize: 14,
    width: '75%',
    color: colors.gray11,
    textAlign: 'center',
    marginBottom: 15,
  },
  cardButton: {
    fontWeight: 800,
    fontSize: 15,
    width: 180,
  },
  cardButtonHighlighted: {
    backgroundColor: '#1DC6D8',
    '&:hover': {
      backgroundColor: '#04ADBF',
    },
  },
  box: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.gray1,
    borderRadius: 6,
    paddingBottom: 25,
    paddingTop: 25,
  },
  boxHighlighted: {
    backgroundColor: 'rgba(29, 198, 216, 0.25)',
  },
});

enum PickSessionTypeModalTestID {
  Container = 'PickSessionTypeModalTestID.Container',
  CloseButton = 'PickSessionTypeModalTestID.CloseButton',
  ConnectCalendar = 'PickSessionTypeModalTestID.ConnectCalendar',
  AddEditClientOpenings = 'PickSessionTypeModalTestID.AddEditClientOpenings',
}

interface Props {
  onClose: () => void;
  onAddCalendarClick: () => void;
  onAddClientOpeningsClick: () => void;
}

export const PickSessionTypeModal: React.FC<Props> = ({
  onClose,
  onAddCalendarClick,
  onAddClientOpeningsClick,
}) => {
  const classes = useStyles();
  const user = useSelector(getCurrentUser);

  const rosterMode = !!user.newClientCapacity || user.newClientCapacity === 0;

  const titleText = rosterMode ? 'Manage availability' : 'Get Started';
  const clientOpeningsBoxTitleText = rosterMode
    ? `You have ${user.newClientCapacity} clients openings available`
    : 'Specify the number of new clients you can book';
  const clientOpeningsBoxButtonText = rosterMode ? 'Edit client openings' : 'Add client openings';

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      fullWidth={false}
      maxWidth="md"
      data-testid={PickSessionTypeModalTestID.Container}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        className={classes.closeButton}
        data-testid={PickSessionTypeModalTestID.CloseButton}
      >
        <CloseIcon />
      </IconButton>

      <div className={classes.dialogContent}>
        <Typography className={classes.title}>{titleText}</Typography>
        <Typography variant="body2" className={classes.subTitle}>
          There are two ways for members to connect with you.
        </Typography>
        <div className={classes.box}>
          <Typography className={classes.cardTitle}>Connect your calendar (Recommended)</Typography>
          <Typography variant="body2" className={classes.cardText}>
            Clients will see your available sessions and book directly through the app.
          </Typography>
          <MHButton
            className={classes.cardButton}
            onClick={() => {
              onClose();
              onAddCalendarClick();
            }}
            data-testid={PickSessionTypeModalTestID.ConnectCalendar}
          >
            Add calendar
          </MHButton>
        </div>

        <Typography className={classes.or}>OR</Typography>

        <div className={`${classes.box} ${rosterMode && classes.boxHighlighted}`}>
          <Typography
            className={`${classes.cardTitle} ${rosterMode && classes.cardTitleHighlighted}`}
          >
            {clientOpeningsBoxTitleText}
          </Typography>
          <Typography variant="body2" className={classes.cardText}>
            You will need to coordinate session bookings over chat with your clients.
          </Typography>
          <MHButton
            className={`${classes.cardButton} ${rosterMode && classes.cardButtonHighlighted}`}
            onClick={() => {
              onClose();
              onAddClientOpeningsClick();
            }}
            data-testid={PickSessionTypeModalTestID.AddEditClientOpenings}
          >
            {clientOpeningsBoxButtonText}
          </MHButton>
        </div>
      </div>
    </Dialog>
  );
};
